/**
 * Internal-note @mention tagging — mirrors Angular `liberty_detectTagging`.
 *
 * Angular builds a `liberty_taggingContent` map (`'@Full Name' -> '[userid:X]'`) as the Liberty
 * user picks mentions from an autocomplete, then replaces each key in the note text before save.
 * Here we build the same map from the active Liberty user list and replace any `@Full Name`
 * occurrence with its `[userid:X]` token, so saved internal notes carry the tag tokens Angular
 * persists (tagged users linked/notified server-side).
 */
import type { LibertyTaggingUser } from '../types'

export type { LibertyTaggingUser }

/** Angular `liberty_applyTagging`: `liberty_taggingContent['@'+name] = `[userid:${user_id}]``. */
export function buildLibertyTaggingMap(users: LibertyTaggingUser[]): Record<string, string> {
  const map: Record<string, string> = {}
  for (const user of users) {
    const name = String(user?.name ?? '').trim()
    if (!name || user?.user_id === undefined || user?.user_id === null) continue
    map[`@${name}`] = `[userid:${user.user_id}]`
  }
  return map
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Angular `liberty_detectTagging`: replace each `@Full Name` key with its `[userid:X]` token.
 * Longer names are replaced first so `@John Doe` isn't partially matched by `@John`.
 */
export function applyLibertyTagging(notes: string, users: LibertyTaggingUser[]): string {
  if (!notes) return notes
  const map = buildLibertyTaggingMap(users)
  let result = notes
  for (const key of Object.keys(map).sort((a, b) => b.length - a.length)) {
    result = result.replace(new RegExp(escapeRegExp(key), 'g'), map[key])
  }
  return result
}
